import {
  InvoiceReadable,
  invoicesListQueryKey,
  invoicesPartialUpdateMutation,
  invoicesRetrieveQueryKey
} from '@crm/api'
import { ContactsInvoiceForm } from '@crm/features/billing/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getFullName, showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { Form, Modal, notification } from 'antd'

import { useEffect } from 'react'

import type { ModalProps } from 'antd'

type Props = {
  invoice?: InvoiceReadable
  onClose: () => void
} & ModalProps

const formName = 'edit-invoice-form'

export const EditInvoiceModal = ({ invoice, onClose, ...props }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutate, isPending } = useMutation({
    ...invoicesPartialUpdateMutation(),
    onSuccess: () => {
      invalidateQueries([
        invoicesRetrieveQueryKey({ path: { id: Number(invoice?.id) } }),

        invoicesListQueryKey()
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
      onClose()
    },
    onError: (error) => showApiErrors(error, form)
  })

  useEffect(() => {
    form.setFieldsValue({
      lead: invoice?.lead?.name,
      contacts: invoice?.contacts.map((contact) => ({
        label: getFullName(contact),
        value: contact?.id
      })),
      additional_emails: invoice?.additional_emails,
      invoice_number: invoice?.invoice_number
    })
  }, [form, invoice])

  const onFormFinish = async () => {
    const values = await form.validateFields()

    const payload = {
      path: { id: Number(invoice?.id) },
      body: {
        ...values,
        lead: values?.lead?.value,
        contacts: values?.contacts?.every(Number)
          ? values.contacts
          : values.contacts?.map((contact: { value: number }) => contact?.value)
      }
    }

    mutate(payload)
  }

  return (
    <Modal
      maskClosable={false}
      title={t('billing.edit_invoice')}
      cancelText={t('buttons.cancel')}
      onCancel={onClose}
      okText={t('buttons.save')}
      okButtonProps={{ htmlType: 'submit', form: formName, loading: isPending }}
      width={600}
      {...props}
    >
      <Form
        form={form}
        name={formName}
        onFinish={onFormFinish}
        layout="vertical"
      >
        <ContactsInvoiceForm
          invoiceId={invoice?.id}
          contractorId={invoice?.lead?.id}
          form={form}
        />
      </Form>
    </Modal>
  )
}
